import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Col, Form, InputGroup, ListGroup, Row, Spinner,
} from 'react-bootstrap';
import {
  ApiTrackDto, ApiTrackLinkDto, ApiTrackPartDto,
} from '../services/trackApiService';
import {
  trackStore, isDefaultTrackId, isGuestCustomTrackId, hasGuestCustomTrack,
  LoginRequiredError,
} from '../services/trackStore';
import * as localFileStore from '../services/localFileStore';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setSelectedTrack } from '../store/appSlice';
import DarkSelect from './DarkSelect';
import ImportExportModal from './ImportExportModal';
import AudioSourcePicker, { AudioSourceValue } from './AudioSourcePicker';

// ── Local form state ──────────────────────────────────────────────────────────

interface FormState {
  name: string;
  bpm: number;
  defaultPitchShift: number;
  audioSourceType: 'youtube' | 'mp3';
  youtubeId: string;
  audioFile: string;
  links: ApiTrackLinkDto[];
  parts: ApiTrackPartDto[];
}

const emptyForm = (): FormState => ({
  name: '',
  bpm: 120,
  defaultPitchShift: 0,
  audioSourceType: 'youtube',
  youtubeId: '',
  audioFile: '',
  links: [],
  parts: [],
});

const dtoToForm = (dto: ApiTrackDto): FormState => ({
  name: dto.name,
  bpm: dto.bpm,
  defaultPitchShift: dto.defaultPitchShift,
  audioSourceType: dto.audioFile ? 'mp3' : 'youtube',
  youtubeId: dto.youtubeId ?? '',
  audioFile: dto.audioFile ?? '',
  links: dto.links.map((l) => ({ title: l.title, url: l.url })),
  parts: dto.parts.map((p) => ({
    title: p.title,
    startTime: p.startTime,
    stopTime: p.stopTime,
    bpmOverride: p.bpmOverride ?? undefined,
  })),
});

// ── YouTube ID validation ────────────────────────────────────────────────────

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const TrackEditor: React.FC<{
  onTracksChanged?: () => void;
  onRequireLogin?: (message: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ onTracksChanged, onRequireLogin, onDirtyChange }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const selectedId = useAppSelector((state) => state.app.backingTrack.selectedTrackId);
  const setSelectedId = useCallback((id: string | null) => dispatch(setSelectedTrack(id)), [dispatch]);

  const [trackList, setTrackList] = useState<ApiTrackDto[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [isNew, setIsNew] = useState(false);

  const [form, setForm] = useState<FormState>(emptyForm());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);
  const [opSuccess, setOpSuccess] = useState<string | null>(null);

  const [ioMode, setIoMode] = useState<'import' | 'export' | null>(null);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Local file (IndexedDB) ────────────────────────────────────────────────
  // File the user just picked in this session (not yet saved to IDB)
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // Whether a blob is already stored in IDB for the currently selected track
  const [hasLocalFile, setHasLocalFile] = useState(false);

  // ── "Add Track" quick-start picker (shown when no track is selected) ──────
  const [addSource, setAddSource] = useState<AudioSourceValue>({
    audioSourceType: 'youtube', youtubeId: '', audioFile: '', bpm: 120,
  });
  const [addPendingFile, setAddPendingFile] = useState<File | null>(null);
  const [addYoutubeTitle, setAddYoutubeTitle] = useState<string | null>(null);
  const addSourceReady =
    (addSource.audioSourceType === 'youtube' && addSource.youtubeId.trim().length > 0) ||
    (addSource.audioSourceType === 'mp3' && (addPendingFile !== null || addSource.audioFile.trim().length > 0));

  const refreshLocalFileStatus = useCallback((id: string | null) => {
    if (!id) { setHasLocalFile(false); return; }
    localFileStore.hasFile(id).then(setHasLocalFile).catch(() => setHasLocalFile(false));
  }, []);

  // ── Load track list ───────────────────────────────────────────────────────

  const loadList = useCallback(() => {
    setListLoading(true);
    setListError(null);
    trackStore.getAll()
      .then((tracks) => {
        setTrackList(tracks.sort((a, b) => a.name.localeCompare(b.name)));
        setListLoading(false);
      })
      .catch((e: Error) => {
        setListError(`Could not load tracks: ${e.message}`);
        setListLoading(false);
      });
  }, []);

  useEffect(() => { loadList(); }, [loadList, isAuthenticated]);

  // Reset the selected track when a guest opens the editor — default tracks
  // are read-only for guests, so starting with no selection avoids confusion.
  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedId(null);
      setIsNew(false);
      setHasLocalFile(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-load track details when selectedId is set (e.g. switched from backing track view)
  useEffect(() => {
    if (selectedId && !isNew) {
      trackStore.getById(selectedId)
        .then((dto) => { setForm(dtoToForm(dto)); setDirty(false); })
        .catch(() => {}); // silently ignore if not found
      setPendingFile(null);
      refreshLocalFileStatus(selectedId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Select a track ────────────────────────────────────────────────────────

  const handleSelect = (id: string) => {
    if (!id) { setSelectedId(null); setIsNew(false); setHasLocalFile(false); return; }
    setOpError(null);
    setOpSuccess(null);
    setDirty(false);
    setPendingFile(null);
    trackStore.getById(id)
      .then((dto) => {
        setSelectedId(id);
        setIsNew(false);
        setForm(dtoToForm(dto));
        refreshLocalFileStatus(id);
      })
      .catch((e: Error) => setOpError(`Failed to load track: ${e.message}`));
  };

  const handleNewTrack = () => {
    setSelectedId(null);
    setIsNew(false);
    setForm(emptyForm());
    setDirty(false);
    setOpError(null);
    setOpSuccess(null);
    setPendingFile(null);
    setHasLocalFile(false);
  };

  const handleAddTrackContinue = () => {
    if (!isAuthenticated && hasGuestCustomTrack()) {
      onRequireLogin?.(
        'You must be signed in to create more than one custom track. Sign in or register to continue.',
      );
      return;
    }
    setSelectedId(null);
    setIsNew(true);
    const derivedName =
      addSource.audioSourceType === 'youtube' && addYoutubeTitle
        ? addYoutubeTitle
        : addSource.audioSourceType === 'mp3' && addSource.audioFile
        ? addSource.audioFile.replace(/\.[^.]+$/, '')
        : '';
    setForm({ ...emptyForm(), ...addSource, name: derivedName });
    setDirty(true);
    setOpError(null);
    setOpSuccess(null);
    setPendingFile(addPendingFile);
    setHasLocalFile(false);
    // Reset the quick-start picker
    setAddSource({ audioSourceType: 'youtube', youtubeId: '', audioFile: '', bpm: 120 });
    setAddPendingFile(null);
    setAddYoutubeTitle(null);
  };

  // ── Form mutation helpers ─────────────────────────────────────────────────

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setOpSuccess(null);
  };

  // Links
  const updateLink = (i: number, patch: Partial<ApiTrackLinkDto>) => {
    update({ links: form.links.map((l, idx) => idx === i ? { ...l, ...patch } : l) });
  };
  const addLink = () => update({ links: [...form.links, { title: '', url: '' }] });
  const removeLink = (i: number) => update({ links: form.links.filter((_, idx) => idx !== i) });
  const moveLink = (i: number, dir: -1 | 1) => {
    const links = [...form.links];
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    [links[i], links[j]] = [links[j], links[i]];
    update({ links });
  };

  // Parts
  const updatePart = (i: number, patch: Partial<ApiTrackPartDto>) => {
    update({ parts: form.parts.map((p, idx) => idx === i ? { ...p, ...patch } : p) });
  };
  const addPart = () => update({
    parts: [...form.parts, { title: 'New Part', startTime: 0, stopTime: 30 }],
  });
  const removePart = (i: number) => update({ parts: form.parts.filter((_, idx) => idx !== i) });
  const movePart = (i: number, dir: -1 | 1) => {
    const parts = [...form.parts];
    const j = i + dir;
    if (j < 0 || j >= parts.length) return;
    [parts[i], parts[j]] = [parts[j], parts[i]];
    update({ parts });
  };

  // ── Save (create or update) ───────────────────────────────────────────────

  const handleSave = async () => {
    setOpError(null);
    setOpSuccess(null);
    setSaving(true);
    const body = {
      name: form.name,
      bpm: form.bpm,
      defaultPitchShift: form.defaultPitchShift,
      youtubeId: form.audioSourceType === 'youtube' ? (form.youtubeId || null) : null,
      audioFile: form.audioSourceType === 'mp3' ? (form.audioFile || null) : null,
      parts: form.parts,
      links: form.links,
    };
    try {
      let savedId: string;
      if (isNew) {
        const created = await trackStore.create(body);
        savedId = created.id;
        setIsNew(false);
        setSelectedId(created.id);
        setOpSuccess(`Track "${created.name}" created successfully.`);
      } else {
        const updated = await trackStore.update(selectedId!, { id: selectedId!, ...body });
        savedId = selectedId!;
        setForm(dtoToForm(updated));
        setOpSuccess(`Track "${updated.name}" saved.`);
      }
      // Persist any locally-selected file blob to IndexedDB
      if (pendingFile) {
        await localFileStore.saveFile(savedId, pendingFile);
        setPendingFile(null);
        setHasLocalFile(true);
      }
      setDirty(false);
      loadList();
      onTracksChanged?.();
    } catch (e: any) {
      if (e instanceof LoginRequiredError) {
        onRequireLogin?.(e.message);
      } else {
        setOpError(`Save failed: ${e.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    setOpError(null);
    setDeleting(true);
    try {
      await localFileStore.removeFile(selectedId).catch(() => {});
      await trackStore.delete(selectedId);
      setSelectedId(null);
      setIsNew(false);
      setForm(emptyForm());
      setDirty(false);
      setHasLocalFile(false);
      setPendingFile(null);
      setOpSuccess('Track deleted.');
      loadList();
      onTracksChanged?.();
    } catch (e: any) {
      setOpError(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const showForm = selectedId !== null || isNew;
  const isGuestDefault = !isAuthenticated && selectedId !== null && isDefaultTrackId(selectedId);
  const isGuestCustom = !isAuthenticated && selectedId !== null && isGuestCustomTrackId(selectedId);
  const readOnly = isGuestDefault;
  const canDelete = isAuthenticated || isGuestCustom;

  return (
    <div className="track-editor">
      {selectedId !== null && (
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-success" onClick={handleNewTrack}>
              + New Track
            </Button>
          </div>
        </div>
      )}

      {!showForm && (
        <div className="mb-4 p-3 border rounded">
          <h6 className="mb-3">Add New Track</h6>
          <AudioSourcePicker
            value={addSource}
            onChange={(patch) => setAddSource((prev) => ({ ...prev, ...patch }))}
            hasLocalFile={false}
            pendingFile={addPendingFile}
            onFilePicked={setAddPendingFile}
            onYoutubeTitleChange={setAddYoutubeTitle}
          />
          <div className="d-flex justify-content-end">
            <Button
              size="sm"
              variant="success"
              disabled={!addSourceReady}
              onClick={handleAddTrackContinue}
            >
              Continue →
            </Button>
          </div>
        </div>
      )}

      <Alert variant="info" className="d-flex align-items-center gap-2">
        <span className="flex-grow-1">
          Share tracks with others.
        </span>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setIoMode('import')}>
            Import
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => setIoMode('export')}>
            Export
          </Button>
        </div>
      </Alert>

      {/* Track selector */}
      <Form.Group className="mb-4">
        <Form.Label>Select Track</Form.Label>
        {listLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            <span className="text-muted small">Loading tracks from API…</span>
          </div>
        ) : listError ? (
          <Alert variant="danger" className="py-2 small">
            {listError}{' '}
            <Button size="sm" variant="link" className="p-0" onClick={loadList}>Retry</Button>
          </Alert>
        ) : (
          <DarkSelect
            value={selectedId ?? ''}
            onChange={(v) => handleSelect(v)}
            placeholder="— choose a track —"
            options={trackList.map((t) => ({ value: t.id, label: t.name }))}
          />
        )}
      </Form.Group>

      {opError && <Alert variant="danger" dismissible onClose={() => setOpError(null)}>{opError}</Alert>}
      {opSuccess && <Alert variant="success" dismissible onClose={() => setOpSuccess(null)}>{opSuccess}</Alert>}

      {!showForm && !opSuccess && (
        <p className="text-muted small">Select a track above, or click <strong>+ New Track</strong>.</p>
      )}

      {showForm && (
        <>
          {readOnly && (
            <Alert variant="info" className="py-2 small mb-3">
              This is a built-in track and is read-only.{' '}
              <Button
                size="sm"
                variant="link"
                className="p-0 align-baseline"
                onClick={() => onRequireLogin?.(
                  'Sign in or register to edit this track and create your own.',
                )}
              >
                Sign in to edit
              </Button>
            </Alert>
          )}
          {dirty && !readOnly && (
            <Alert variant="warning" className="py-2 small mb-3">
              You have unsaved changes.
            </Alert>
          )}

          {/* ── Audio Source ── */}
          <AudioSourcePicker
            value={{
              audioSourceType: form.audioSourceType,
              youtubeId: form.youtubeId,
              audioFile: form.audioFile,
              bpm: form.bpm,
            }}
            onChange={(patch: Partial<AudioSourceValue>) => update(patch)}
            hasLocalFile={hasLocalFile}
            pendingFile={pendingFile}
            onFilePicked={setPendingFile}
          />

          {/* ── Name ── */}
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={form.name}
              placeholder="Track name"
              onChange={(e) => update({ name: e.target.value })}
            />
          </Form.Group>

          {/* ── Core metadata ── */}
          <Form.Group className="mb-3">
            <Form.Label>
              Default Pitch Shift (semitones): {form.defaultPitchShift > 0 ? '+' : ''}{form.defaultPitchShift}
            </Form.Label>
            <Form.Range
              value={form.defaultPitchShift}
              min={-12}
              max={12}
              step={1}
              onChange={(e) => update({ defaultPitchShift: parseInt(e.target.value, 10) })}
              disabled={form.audioSourceType === 'youtube'}
            />
            {form.audioSourceType === 'youtube' && (
              <Form.Text className="text-warning">
                Pitch shift is not supported for YouTube playback. Switch the audio source to MP3 to enable this feature.
              </Form.Text>
            )}
          </Form.Group>

          {/* ── Links ── */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Links</strong>
              <Button size="sm" variant="outline-secondary" onClick={addLink}>+ Add Link</Button>
            </div>
            {form.links.length === 0 && <p className="text-muted small">No links yet.</p>}
            <ListGroup>
              {form.links.map((link, i) => (
                <ListGroup.Item key={i} className="py-2">
                  <Row className="g-2 align-items-center">
                    <Col sm={4}>
                      <Form.Control
                        size="sm"
                        placeholder="Label"
                        value={link.title}
                        onChange={(e) => updateLink(i, { title: e.target.value })}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        size="sm"
                        placeholder="URL"
                        value={link.url}
                        onChange={(e) => updateLink(i, { url: e.target.value })}
                      />
                    </Col>
                    <Col xs="auto" className="d-flex gap-1">
                      <Button size="sm" variant="outline-secondary" onClick={() => moveLink(i, -1)} disabled={i === 0}>↑</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => moveLink(i, 1)} disabled={i === form.links.length - 1}>↓</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removeLink(i)}>✕</Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>

          {/* ── Parts ── */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Parts</strong>
              <Button size="sm" variant="outline-secondary" onClick={addPart}>+ Add Part</Button>
            </div>
            {form.parts.length === 0 && <p className="text-muted small">No parts yet.</p>}
            <ListGroup>
              {form.parts.map((part, i) => (
                <ListGroup.Item key={i} className="py-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Form.Control
                      size="sm"
                      placeholder="Part title"
                      value={part.title}
                      onChange={(e) => updatePart(i, { title: e.target.value })}
                      className="me-2"
                    />
                    <div className="d-flex gap-1 flex-shrink-0">
                      <Button size="sm" variant="outline-secondary" onClick={() => movePart(i, -1)} disabled={i === 0}>↑</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => movePart(i, 1)} disabled={i === form.parts.length - 1}>↓</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => removePart(i)}>✕</Button>
                    </div>
                  </div>
                  <Row className="g-2">
                    <Col sm={4}>
                      <Form.Label className="small mb-1">Start ({formatSeconds(part.startTime)})</Form.Label>
                      <InputGroup size="sm">
                        <Form.Control
                          type="number"
                          min={0}
                          value={part.startTime}
                          onChange={(e) => updatePart(i, { startTime: parseInt(e.target.value, 10) || 0 })}
                        />
                        <InputGroup.Text>s</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col sm={4}>
                      <Form.Label className="small mb-1">Stop ({formatSeconds(part.stopTime)})</Form.Label>
                      <InputGroup size="sm">
                        <Form.Control
                          type="number"
                          min={0}
                          value={part.stopTime}
                          onChange={(e) => updatePart(i, { stopTime: parseInt(e.target.value, 10) || 0 })}
                        />
                        <InputGroup.Text>s</InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col sm={4}>
                      <Form.Label className="small mb-1">BPM Override (optional)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={20}
                        max={400}
                        placeholder="—"
                        value={part.bpmOverride ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          updatePart(i, { bpmOverride: val === '' ? undefined : parseInt(val, 10) });
                        }}
                      />
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>

          {/* ── Actions ── */}
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !dirty || readOnly}
            >
              {saving ? <><Spinner size="sm" className="me-1" />Saving…</> : (isNew ? 'Create Track' : 'Save Changes')}
            </Button>
            {!isNew && canDelete && (
              <Button
                variant="outline-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <><Spinner size="sm" className="me-1" />Deleting…</> : 'Delete Track'}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={() => { setSelectedId(null); setIsNew(false); setDirty(false); }}
            >
              Cancel
            </Button>
          </div>
        </>
      )}

      <ImportExportModal
        show={ioMode !== null}
        mode={ioMode ?? 'export'}
        onHide={() => setIoMode(null)}
        isAuthenticated={isAuthenticated}
        onImported={() => { loadList(); onTracksChanged?.(); }}
      />
    </div>
  );
};

export default TrackEditor;
